import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { IconCloseSvg } from '../../../assets/inventory';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { SoundName } from '../../../utils/sound';
import { ACHIEVEMENTS_FONT as FONT, achievementsColors } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';

/**
 * The pieces "Modal / Settings" (5054:78827, page "PAVEL DRAFT") shares with the popups its rows
 * open - Help, Invitations, Game Rules and Personal Data. They are drawn as its siblings: the same
 * 358-wide dark maroon panel floating over the screen, the centred gold title with the close glyph,
 * the surface/raised plates with the orange hairline, the green toggle and the gold divider. Every
 * number is in the 390-wide design space and is scaled once by the caller's `scale`.
 */
export const POPUP = { radius: 10, paddingX: 12, paddingY: 14, gap: 12 };
/** The design returns no fill for the panel; the artboard reads as this darkened backdrop. */
export const POPUP_TINT = 'rgba(26,6,3,0.82)';
const HEADER = { height: 40, button: 32, closeIcon: 24 };
const TOGGLE = { width: 44, height: 24, knob: 20, inset: 2 };

export const popupColors = {
	gold50: achievementsColors.gold50,
	gold200: achievementsColors.gold200,
	primary: achievementsColors.primary,
	secondary: achievementsColors.secondary,
	muted: achievementsColors.muted,
	/** surface/raised - every row's and plate's fill. */
	raised: achievementsColors.surface,
	/** The rows' and plates' orange hairline. */
	plateBorder: achievementsColors.panelOrange,
	/** surface/sunken - a well inside a plate. */
	sunken: achievementsColors.sunken,
	/** border/gold - the dropdown's outline and the dividers. */
	borderGold: '#E89200',
	/** state/success/base - a toggle that is on. */
	toggleOn: '#0CEB66',
	/** surface/overlay + icon/stroke - a toggle that is off. */
	toggleOff: achievementsColors.overlay,
	toggleOffBorder: '#C88200',
	onGold: achievementsColors.onGold,
	goldRim: achievementsColors.goldStroke,
} as const;

/** The popup body: the panel's padding, then the header and whatever follows it. */
export const popupBodyStyle = (scale: number) => ({
	flex: 1,
	paddingHorizontal: POPUP.paddingX * scale,
	paddingVertical: POPUP.paddingY * scale,
	gap: POPUP.gap * scale,
});

/** The 40-tall header: the title centred between an empty slot and the close glyph. */
export const PopupHeader = ({
	title,
	onClose,
	closeLabel,
	scale,
}: {
	title: string;
	onClose: () => void;
	closeLabel: string;
	scale: number;
}) => {
	const s = (n: number) => n * scale;
	const button = { width: s(HEADER.button), height: s(HEADER.button) };
	return (
		<View style={[headerStyles.header, { height: s(HEADER.height) }]}>
			<View style={[headerStyles.button, button]} />
			<Text
				style={[headerStyles.title, { fontSize: s(24), lineHeight: s(24 * 1.3) }]}
				numberOfLines={1}
			>
				{title}
			</Text>
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={closeLabel}
				onPress={onClose}
				hitSlop={8}
				style={[headerStyles.button, button]}
			>
				<IconCloseSvg width={s(HEADER.closeIcon)} height={s(HEADER.closeIcon)} />
			</Pressable>
		</View>
	);
};

/** Server HTML (help copy, game rules) in the popups' type ramp. */
export const createPopupHtmlStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const paragraph = {
		fontFamily: FONT,
		fontWeight: '500' as const,
		fontSize: s(14),
		lineHeight: s(20),
		color: popupColors.secondary,
	};
	const heading = {
		fontFamily: FONT,
		fontWeight: '700' as const,
		fontSize: s(18),
		lineHeight: s(18 * 1.3),
		color: popupColors.gold200,
		marginTop: 0,
		marginBottom: s(6),
		textTransform: 'uppercase' as const,
	};
	return {
		h1: heading,
		h2: heading,
		h3: {
			fontFamily: FONT,
			fontWeight: '700' as const,
			fontSize: s(16),
			lineHeight: s(20),
			color: popupColors.gold50,
			marginTop: s(4),
			marginBottom: s(4),
		},
		p: { ...paragraph, marginTop: 0, marginBottom: s(12) },
		ul: { marginTop: 0, marginBottom: s(12), paddingLeft: s(16) },
		ol: { marginTop: 0, marginBottom: s(12), paddingLeft: s(16) },
		li: { ...paragraph, marginBottom: s(6) },
		a: { color: popupColors.gold200, textDecorationLine: 'underline' as const },
		strong: { color: popupColors.gold50, fontWeight: '700' as const },
		b: { color: popupColors.gold50, fontWeight: '700' as const },
	};
};

/**
 * "Toggle" (5052:13296). On = state/success/base track with the white knob right; off =
 * surface/overlay track with an icon/stroke hairline and the text/muted knob left.
 *
 * Animated with core `Animated` (Reanimated worklets no-op in the web build): the knob springs
 * across with a little overshoot, squashes while pressed, the track and knob colours cross-fade,
 * a green glow blooms behind the track and a ring pulses off the knob when it turns on. Colours
 * cannot run on the native driver, so everything here stays on the JS one.
 */
export const PopupToggle = ({
	value,
	onChange,
	scale,
	// `false` for a switch that plays its own effect - the sound toggle itself.
	sound = 'ToggleSound',
}: {
	value: boolean;
	onChange: () => void;
	scale: number;
	sound?: SoundName | false;
}) => {
	const s = (n: number) => n * scale;
	const progress = useRef(new Animated.Value(value ? 1 : 0)).current;
	const press = useRef(new Animated.Value(0)).current;
	const ripple = useRef(new Animated.Value(0)).current;
	const first = useRef(true);

	useEffect(() => {
		Animated.spring(progress, {
			toValue: value ? 1 : 0,
			friction: 6,
			tension: 140,
			useNativeDriver: false,
		}).start();
		// No pulse for the value the modal opens with, only for a real flip to on.
		if (first.current) {
			first.current = false;
			return;
		}
		if (value) {
			ripple.setValue(0);
			Animated.timing(ripple, {
				toValue: 1,
				duration: 520,
				easing: Easing.out(Easing.cubic),
				useNativeDriver: false,
			}).start();
		}
	}, [progress, ripple, value]);

	const pressTo = (toValue: number) =>
		Animated.spring(press, { toValue, friction: 5, tension: 220, useNativeDriver: false }).start();

	const knob = s(TOGGLE.knob);
	const travel = s(TOGGLE.width - TOGGLE.knob - 2 * TOGGLE.inset);
	const hairline = Math.max(StyleSheet.hairlineWidth, s(0.5));

	return (
		<Pressable
			accessibilityRole='switch'
			accessibilityState={{ checked: value }}
			sound={sound}
			onPress={onChange}
			onPressIn={() => pressTo(1)}
			onPressOut={() => pressTo(0)}
			hitSlop={6}
			style={{ width: s(TOGGLE.width), height: s(TOGGLE.height) }}
		>
			<Animated.View
				pointerEvents='none'
				style={[
					toggleStyles.glow,
					{
						borderRadius: s(TOGGLE.height / 2),
						opacity: progress.interpolate({
							inputRange: [0, 1],
							outputRange: [0, 1],
							extrapolate: 'clamp',
						}),
						shadowRadius: s(8),
					},
				]}
			/>
			<Animated.View
				style={[
					StyleSheet.absoluteFill,
					{
						borderRadius: s(TOGGLE.height / 2),
						backgroundColor: progress.interpolate({
							inputRange: [0, 1],
							outputRange: [popupColors.toggleOff, popupColors.toggleOn],
							extrapolate: 'clamp',
						}),
					},
				]}
			/>
			{/* The off state's hairline fades rather than snapping, so the knob never shifts. */}
			<Animated.View
				pointerEvents='none'
				style={[
					StyleSheet.absoluteFill,
					{
						borderRadius: s(TOGGLE.height / 2),
						borderWidth: hairline,
						borderColor: popupColors.toggleOffBorder,
						opacity: progress.interpolate({
							inputRange: [0, 1],
							outputRange: [1, 0],
							extrapolate: 'clamp',
						}),
					},
				]}
			/>
			<Animated.View
				pointerEvents='none'
				style={{
					position: 'absolute',
					top: s(TOGGLE.inset),
					left: s(TOGGLE.inset),
					width: knob,
					height: knob,
					transform: [
						{ translateX: progress.interpolate({ inputRange: [0, 1], outputRange: [0, travel] }) },
					],
				}}
			>
				<Animated.View
					style={[
						StyleSheet.absoluteFill,
						{
							borderRadius: knob / 2,
							borderWidth: s(1.5),
							borderColor: popupColors.toggleOn,
							opacity: ripple.interpolate({
								inputRange: [0, 0.15, 1],
								outputRange: [0, 0.8, 0],
							}),
							transform: [
								{ scale: ripple.interpolate({ inputRange: [0, 1], outputRange: [1, 2.3] }) },
							],
						},
					]}
				/>
				<Animated.View
					style={[
						toggleStyles.knob,
						{
							borderRadius: knob / 2,
							backgroundColor: progress.interpolate({
								inputRange: [0, 1],
								outputRange: [popupColors.muted, popupColors.primary],
								extrapolate: 'clamp',
							}),
							transform: [
								{ scaleX: press.interpolate({ inputRange: [0, 1], outputRange: [1, 1.22] }) },
								{ scaleY: press.interpolate({ inputRange: [0, 1], outputRange: [1, 0.88] }) },
							],
						},
					]}
				/>
			</Animated.View>
		</Pressable>
	);
};

/** "Divider": a 1pt border/gold rule that fades out over its first and last fifth. */
export const PopupDivider = ({ scale }: { scale: number }) => {
	const id = useSvgId('settingsDivider');
	return (
		<Svg width='100%' height={Math.max(1, scale)}>
			<Defs>
				<LinearGradient id={id} x1='0' y1='0' x2='1' y2='0'>
					<Stop offset='0' stopColor={popupColors.borderGold} stopOpacity={0} />
					<Stop offset='0.2' stopColor={popupColors.borderGold} stopOpacity={0.9} />
					<Stop offset='0.8' stopColor={popupColors.borderGold} stopOpacity={0.9} />
					<Stop offset='1' stopColor={popupColors.borderGold} stopOpacity={0} />
				</LinearGradient>
			</Defs>
			<Rect width='100%' height='100%' fill={`url(#${id})`} />
		</Svg>
	);
};

const headerStyles = StyleSheet.create({
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	button: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	title: {
		flex: 1,
		textAlign: 'center',
		fontFamily: FONT,
		fontWeight: '700',
		color: popupColors.gold50,
	},
});

const toggleStyles = StyleSheet.create({
	glow: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: popupColors.toggleOn,
		shadowColor: popupColors.toggleOn,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.75,
	},
	knob: {
		...StyleSheet.absoluteFillObject,
		shadowColor: '#000000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.3,
		shadowRadius: 2,
		elevation: 2,
	},
});
